/**
 * IMPORTANT: This file is only for handling instore module.
 * Other modules are now managed in the merchant-portal project.
 *
 * Authentication is already handled in merchant-portal - the implementation
 * here is legacy only and will be removed in the future.
 * Only URLs will be maintained.
 *
 * Permissions here are only for instore and use legacy permission_scopes.
 * New permissions are in permission_scopes_v2.
 *
 * If you need to modify login auth functionality, please consult with the
 * merchant-services team.
 */

import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import Auth from '../../Utils/Auth/auth';
import myOwnRoutes from '../../containers/routes/index';
import { useLaunchDarkly } from '../LaunchDarkly/LDContext';

const scope_channels = ['payment_code', 'generate_orders', 'history_orders'];

const scope_history = [
  'merchant_transactions',
  'bulk_refunds',
  'merchants_payments',
];

const configScopes = ['stores', 'users', 'campaigns'];

const defaultContext = {
  loadPermissions: () => {},
  getPermission: () => {},
  menus: [],
  routes: [],
  openModalRoot: false,
  hotjarIdentified: false,
  setOpenModalRoot: () => {},
  clearPermissions: () => {},
  setHotjarIdentified: () => {},
};

const PermissionContext = React.createContext(defaultContext);

const PermissionProvider = ({ children }) => {
  const [permissionScopes, setPermissionScopes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [openModalRoot, setOpenModalRoot] = useState(false);
  const [hotjarIdentified, setHotjarIdentified] = useState(false);

  const { initializeLaunchDarkly, loadFlags, instoreModuleV2Enabled } =
    useLaunchDarkly();

  const { merchant } = Auth.userData();
  const merchantChannels = merchant?.channels || [];

  const getPermission = module => {
    const permission = permissionScopes?.find(
      scope => scope.module_name === module
    );
    const hasScope = scope => {
      return permission.scopes.includes(scope);
    };
    if (!permission) return null;
    return { ...permission, hasScope };
  };

  useEffect(() => {
    if (Auth.userLoggedIn()) resolvePermissions(permissionScopes);
  }, [permissionScopes]);

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    const { permission_scopes } = Auth.userData();
    if (!Auth.userLoggedIn()) return;

    try {
      const isInitialized = await initializeLaunchDarkly();
      if (isInitialized) {
        await loadFlags();
      }
    } catch (error) {
      console.error('Failed to initialize LaunchDarkly:', error);
    }

    resolvePermissions(permission_scopes);
    setPermissionScopes(permission_scopes);
  };

  const clearPermissions = () => {
    setPermissionScopes([]);
    setMenus([]);
    setRoutes([]);
    setHotjarIdentified(false);
  };

  const resolvePermissions = permissions => {
    if (!Auth.userLoggedIn()) return;

    const { menus, routes } = myOwnRoutes(
      scope_channels,
      scope_history,
      configScopes,
      permissions,
      merchantChannels,
      instoreModuleV2Enabled
    );

    setMenus(menus);
    setRoutes(routes);
    return;
  };

  return (
    <PermissionContext.Provider
      value={{
        openModalRoot,
        hotjarIdentified,
        setOpenModalRoot,
        getPermission,
        loadPermissions,
        clearPermissions,
        setHotjarIdentified,
        menus,
        routes,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

PermissionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const usePermissionContext = () => {
  const context = React.useContext(PermissionContext);
  if (context === undefined) {
    throw new Error(
      'usePermissionContext must be used within a PermissionProvider'
    );
  }
  return context;
};

export { PermissionProvider, usePermissionContext };
